import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { useCreateBusinessCard } from '@/api/api-mutations'
import { useListCompanies, useListCustomers } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { SectionRow } from '@/components/base/section-row'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { useDebounce } from '@/utils/hooks'

type LockedCompany = { uuid: string; name: string }

export function CreateBusinessCardForm({ lockedCompany }: { lockedCompany?: LockedCompany }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const navigate = useNavigate()

	const createCard = useCreateBusinessCard()

	const [created, setCreated] = useState<{ uuid: string; pin: string } | null>(null)

	const [companySearch, setCompanySearch] = useState('')
	const debouncedCompanySearch = useDebounce(companySearch)
	const companies = useListCompanies(
		{ limit: 20, offset: 0, q: debouncedCompanySearch || undefined },
		{ enabled: !lockedCompany && debouncedCompanySearch.length > 0 }
	)

	const [customerSearch, setCustomerSearch] = useState('')
	const debouncedCustomerSearch = useDebounce(customerSearch)
	const customers = useListCustomers(
		{ limit: 20, offset: 0, q: debouncedCustomerSearch || undefined },
		{ enabled: debouncedCustomerSearch.length > 0 }
	)

	const companyOptions = useMemo(
		() => (companies.data?.companies ?? []).map(c => ({ value: c.uuid, label: c.name })),
		[companies.data]
	)

	const customerOptions = useMemo(() => {
		return (customers.data?.customers ?? []).map(c => {
			const name = [c.firstName, c.lastName].filter(Boolean).join(' ')
			return { value: c.uuid, label: name || c.email || L.Customers.Anonymous() }
		})
	}, [customers.data, L])

	const form = useAppForm({
		defaultValues: {
			name: '',
			cardNumber: '',
			externalId: '',
			companyUuid: lockedCompany?.uuid ?? '',
			customerUuid: ''
		},
		onSubmit: async ({ value }) => {
			const companyUuid = lockedCompany?.uuid ?? value.companyUuid
			const result = await createCard({
				path: { companyUuid },
				body: {
					name: value.name,
					cardNumber: value.cardNumber,
					externalId: value.externalId || undefined,
					customerUuid: value.customerUuid || undefined
				}
			})
			if (result.isSuccess) {
				setCreated({ uuid: result.response.uuid, pin: result.response.pin })
			} else {
				toast.error(L.Error.SomethingWentWrong())
			}
		}
	})

	if (created) {
		return (
			<>
				<Drawer.Header>
					<Drawer.Title asChild>
						<Heading>{L.BusinessCards.Create.PinTitle()}</Heading>
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body className='flex-1 overflow-auto'>
					<div className='flex flex-col gap-y-3'>
						<Text size='small' leading='compact' className='text-ui-fg-subtle'>
							{L.BusinessCards.Create.PinDescription()}
						</Text>
						<div className='rounded-md border border-ui-border-base bg-ui-bg-subtle px-4 py-3 text-center'>
							<Text size='xlarge' weight='plus' leading='compact' className='font-mono tracking-widest'>
								{created.pin}
							</Text>
						</div>
					</div>
				</Drawer.Body>
				<Drawer.Footer>
					<div className='flex items-center justify-end gap-x-2'>
						<Button variant='secondary' size='small' onClick={() => handleSuccess()}>
							{L.General.Close()}
						</Button>
						<Button
							size='small'
							onClick={() =>
								navigate({
									to: '/business-cards/$cardUuid',
									params: { cardUuid: created.uuid },
									replace: true
								})
							}
						>
							{L.BusinessCards.Create.GoToCard()}
						</Button>
					</div>
				</Drawer.Footer>
			</>
		)
	}

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.BusinessCards.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						{lockedCompany ? (
							<SectionRow title={L.BusinessCards.Detail.Company()} value={lockedCompany.name} />
						) : (
							<form.AppField
								name='companyUuid'
								validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
								children={field => (
									<field.SingleComboboxField
										label={L.BusinessCards.Detail.Company()}
										placeholder={L.BusinessCards.Create.SelectCompany()}
										options={companyOptions}
										onSearchValueChange={setCompanySearch}
									/>
								)}
							/>
						)}
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Name()}
									placeholder={L.BusinessCards.Create.NamePlaceholder()}
								/>
							)}
						/>
						<form.AppField
							name='cardNumber'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									label={L.BusinessCards.Column.CardNumber()}
									placeholder={L.BusinessCards.Create.CardNumberPlaceholder()}
								/>
							)}
						/>
						<form.AppField
							name='customerUuid'
							children={field => (
								<field.SingleComboboxField
									label={L.BusinessCards.Detail.Customer()}
									placeholder={L.BusinessCards.Create.SelectCustomer()}
									options={customerOptions}
									onSearchValueChange={setCustomerSearch}
									optional
								/>
							)}
						/>
						<form.AppField
							name='externalId'
							children={field => <field.TextField label={L.BusinessCards.Column.ExternalId()} optional />}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
